import {
  MEDICAL_REVIEW_NEUTRAL_STATUS,
  type CoachCheckinState,
  type CoachNow,
} from "@care-by-ella/contracts";

// TES-1074 point 5 — the coach's "Aktuellt" as sentences.
//
// Pure, so the wording rules are testable without a DOM. Two of them come
// from A-01 and are easy to lose:
//   - a card must not say there is nothing new without saying since when
//     ("får inte samtidigt säga 'Inget noterat sedan sist' utan att förklara varför");
//   - a late or missing check-in is never phrased as a failing.

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many.replace("{n}", String(n));
}

export function fmtDay(iso: string): string {
  return new Date(iso).toLocaleDateString("sv-SE", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

export const COACH_INACTIVE_LINE =
  "Coach ingår inte i patientens abonnemang – ingen ny coachkontakt.";

// TES-1121 (Ö-342 a) — inactive because she has never had a subscription.
export const COACH_BEFORE_SUBSCRIPTION_LINE =
  "Inget abonnemang än – coachchatten öppnar när abonnemanget startar.";

export function coachToDoLines(now: CoachNow): string[] {
  if (now.coachAccess === "inactive")
    return [
      now.coachOpensWithSubscription
        ? COACH_BEFORE_SUBSCRIPTION_LINE
        : COACH_INACTIVE_LINE,
    ];
  const lines: string[] = [];
  const { openCheckins, unreadMessages, dueFollowUps } = now.toDo;
  if (openCheckins > 0) {
    lines.push(
      plural(
        openCheckins,
        "Svara på en veckoincheckning",
        "Svara på {n} veckoincheckningar",
      ),
    );
  }
  if (unreadMessages > 0) {
    lines.push(
      plural(
        unreadMessages,
        "Svara på ett meddelande i chatten",
        "Svara på {n} meddelanden i chatten",
      ),
    );
  }
  if (dueFollowUps > 0) {
    lines.push(
      plural(
        dueFollowUps,
        "En uppföljning ska göras nu",
        "{n} uppföljningar ska göras nu",
      ),
    );
  }
  return lines.length > 0 ? lines : ["Inget väntar på dig just nu."];
}

export function coachChangeLines(now: Pick<CoachNow, "changes">): string[] {
  const { since, checkins, messages, memoryRows } = now.changes;
  const parts: string[] = [];
  if (checkins > 0) {
    parts.push(plural(checkins, "en ny incheckning", "{n} nya incheckningar"));
  }
  if (messages > 0) {
    parts.push(plural(messages, "ett nytt meddelande", "{n} nya meddelanden"));
  }
  if (memoryRows > 0) {
    parts.push(
      plural(
        memoryRows,
        "en ny uppgift i Patientminnet",
        "{n} nya uppgifter i Patientminnet",
      ),
    );
  }
  if (since === null) {
    return parts.length > 0
      ? [`Ingen coachkontakt ännu. Hittills: ${parts.join(", ")}.`]
      : ["Ingen coachkontakt ännu och inget inskickat."];
  }
  return parts.length > 0
    ? [`Sedan ditt senaste meddelande ${fmtDay(since)}: ${parts.join(", ")}.`]
    : [`Inget nytt sedan ditt senaste meddelande ${fmtDay(since)}.`];
}

// TES-1214 — the work-queue card's "Förändring sedan senaste kontakten" when
// Patientminnet has no change row: Aktuellt's sentence, which says since when
// nothing is new, or that there has been no coach contact yet. Only a server
// that predates `changesSince` leaves the card with the old bare line.
export const CARD_NO_CHANGE_FALLBACK = "Inget noterat sedan sist.";

export function cardChangeLine(
  changes: CoachNow["changes"] | undefined,
): string {
  return changes ? coachChangeLines({ changes })[0] : CARD_NO_CHANGE_FALLBACK;
}

// The check-in statuses A-01 names, verbatim:
// "Behöver svar; Utkast sparat; Besvarad; Avslutad utan svar"
// A routed one shows the existing neutral line.
export const CHECKIN_STATE_LABEL: Record<CoachCheckinState, string> = {
  open: "Behöver svar",
  answered: "Besvarad",
  closed_without_reply: "Avslutad utan svar",
  medical: MEDICAL_REVIEW_NEUTRAL_STATUS,
};

export function latestCheckinLine(now: CoachNow): string {
  const c = now.latestCheckin;
  if (!c) return "Ingen veckoincheckning inskickad än";
  return `${fmtDay(c.submittedAt)} · ${CHECKIN_STATE_LABEL[c.state]}`;
}

// TES-1118 — the server sends no window for a patient without an active
// subscription, because she has no weekly check-in (Ö-62, TES-1021).
export const NO_CHECKIN_WITHOUT_SUBSCRIPTION_LINE =
  "Ingen – veckoincheckningen ingår i abonnemanget, som inte är aktivt";

export function nextCheckinLine(now: CoachNow): string {
  const n = now.nextCheckin;
  if (!n) return NO_CHECKIN_WITHOUT_SUBSCRIPTION_LINE;
  return n.openNow
    ? `Öppen sedan ${fmtDay(n.opensAt)} – inte inskickad än`
    : `Öppnar ${fmtDay(n.opensAt)} kl. 09.00`;
}

export function latestContactLine(now: CoachNow): string {
  const { coachAt, patientAt } = now.latestContact;
  const coach = coachAt ? `du ${fmtDay(coachAt)}` : "du har inte skrivit än";
  const patient = patientAt
    ? `patienten ${fmtDay(patientAt)}`
    : "patienten har inte skrivit än";
  return `Senast i chatten: ${coach}, ${patient}`;
}
